/**
 * @file `OutOfTimeTakeover` — the calm full-pane treatment shown in the
 *       lab pane when a free learner's daily VM-time budget is spent
 *       (design §5 `outoftime`; #941, reworked for the interim in #1315).
 * @module @labcraft/lab/ui/components/out-of-time-takeover
 *
 * A doorway, not a slammed door — and, until a payment path exists, an
 * HONEST one: it no longer offers a purchase it can't fulfil. It leads with
 * the reassurance (the machine is snapshotted, per #926) and the concrete
 * LOCAL reset time, and — when Web Push is available — offers to ping the
 * learner the moment their free time resets so they can resume, reusing the
 * same {@link PushClient} opt-in the at-capacity wall uses. When push is
 * dark, unsupported, or declined, the static reset-time copy stands alone as
 * the always-present fallback.
 *
 * Renders for the `budget-exhausted` WS frame (mid-session exhaustion or a
 * pre-open refusal), driven from {@link TerminalOverlay}. On mount it fires
 * the funnel's `outOfTimeShown` beat.
 */

import { useEffect, useState, type JSX } from 'react'
import { track } from '@labcraft/ui/hooks'
import type { PushVapidResponse } from '../../shared/routes.ts'
import { PushClient } from '../lib/push_client.ts'
import {
  BUDGET_EVENTS,
  formatResetClock,
  formatResetCountdown,
  nextBudgetReset,
} from '../lib/budget.ts'
import styles from './out_of_time_takeover.module.css'

/**
 * Which face of the wall is showing.
 *   - `prompt` — reset-time copy + (when push is offerable) the opt-in.
 *   - `enabled` — the resume-reminder opt-in succeeded.
 *   - `denied` — push declined / failed; the static copy stands alone.
 */
type Phase = 'prompt' | 'enabled' | 'denied'

/**
 * The out-of-time lab-pane takeover. See the file header.
 *
 * @returns The takeover element.
 */
export function OutOfTimeTakeover(): JSX.Element {
  // Snapshot the reset at render — a fixed "resets 5:00 PM (in 6h 12m)" beat,
  // not a live ticking countdown.
  const now = new Date()
  const reset = nextBudgetReset(now)
  const resetClock = formatResetClock(reset)
  const countdown = formatResetCountdown(reset, now)

  const [publicKey, setPublicKey] = useState('')
  const [phase, setPhase] = useState<Phase>('prompt')

  useEffect(() => {
    track(BUDGET_EVENTS.outOfTimeShown)
  }, [])

  // Fetch the VAPID public key on mount. Empty (push dark) or a fetch failure
  // leaves the key empty, which hides the opt-in — the static copy covers it.
  useEffect(() => {
    let cancelled = false
    fetch('/api/push/vapid', { credentials: 'include' })
      .then(res => (res.ok ? (res.json() as Promise<PushVapidResponse>) : null))
      .then(data => {
        if (!cancelled && data) setPublicKey(data.publicKey)
      })
      .catch(() => {
        // Push stays unavailable — the static reset-time copy is the fallback.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Offer the opt-in only when a keypair exists, the browser supports push, and
  // permission isn't already granted. Notification may be undefined in
  // non-browser test envs, so guard the permission read.
  const permission =
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  const canOfferPush =
    publicKey !== '' && PushClient.isSupported() && permission !== 'granted'

  /** Run the resume-reminder opt-in; flip the card to `enabled` or `denied`. */
  async function onNotify(): Promise<void> {
    const ok = await PushClient.enableResumeReminder(publicKey)
    setPhase(ok ? 'enabled' : 'denied')
  }

  return (
    <div className={styles.takeover} role="status" aria-live="polite">
      <p className={styles.heading}>Today&rsquo;s free lab time is up.</p>
      <p className={styles.body}>
        Your machine is snapshotted exactly where you are &mdash;
        nothing&rsquo;s lost. Your free lab time resets at{' '}
        <b className={styles.clock}>{resetClock}</b> your time (in {countdown}).
      </p>
      {phase === 'prompt' && canOfferPush && (
        <button
          type="button"
          className={styles.cta}
          onClick={() => void onNotify()}>
          Notify me when I can resume
        </button>
      )}
      {phase === 'enabled' && (
        <p className={styles.body}>
          We&rsquo;ll ping you the moment your lab time resets &mdash; you can
          close this tab.
        </p>
      )}
    </div>
  )
}
